// src/mixins/form-field.js
export default {
  components: {
    FormField: () => import(/* webpackChunkName: "formField" */ '@/components/FormField'),
  },

  data () {
    return {
      inputTimeout: null,
      retry: 3,
      validateOnBlur: true,
    }
  },

  props: {
    field: {
      required: true,
      type: Object,
    },
  },

  mounted () {
    // Defensive: ensure field.props exists and has a rules array
    if (!this.field) return

    if (!this.field.props || typeof this.field.props !== 'object') {
      // create an empty props object so other code can rely on it
      this.$set(this.field, 'props', {})
    }

    // ensure rules is an array
    if (!Array.isArray(this.field.props.rules)) {
      this.$set(this.field.props, 'rules', [])
    }

    // push validation rule but safely (avoid duplicate pushes)
    try {
      // add one rule that returns this.field.error if present
      const ruleExists = this.field.props.rules.some(r => r && r.toString && r.toString().includes('field.error'))
      if (!ruleExists) {
        this.field.props.rules.push(() => this.field.error ? this.field.error : true)
      }
    } catch (e) {
      // fail silently — defensive fallback
      console.warn('[form-field] could not add rule', e)
    }

    // start polling for the internal vuetify input(s)
    this.inputTimeout = setInterval(this.inputReady, 500)
  },

  beforeDestroy () {
    // clear the polling interval
    if (this.inputTimeout) {
      clearInterval(this.inputTimeout)
      this.inputTimeout = null
    }
  },

  methods: {
    inputReady () {
      const inputs = this.getInput()

      if (inputs.length === 1) {
        clearInterval(this.inputTimeout)
        this.inputTimeout = null

        // copy validate/reset from the found input
        try {
          this.$set(this.field, 'validate', inputs[0].validate)
          this.$set(this.field, 'reset', inputs[0].reset)
        } catch (e) {
          // some vuetify internals might not expose validate/reset — ignore
        }
      } else if (inputs.length > 1) {
        clearInterval(this.inputTimeout)
        this.inputTimeout = null
      } else {
        if (this.retry-- <= 0) {
          clearInterval(this.inputTimeout)
          this.inputTimeout = null
        }
      }
    },

    getInput () {
      const results = []

      // safer recursive search that checks children existence
      const search = (children, depth = 0) => {
        if (!children || !children.length) {
          if (depth === 0) return results
          return
        }

        for (const child of children) {
          if (!child) continue
          // vuetify input expose errorBucket — that's our heuristic to find input components
          if (Object.prototype.hasOwnProperty.call(child, 'errorBucket')) {
            results.push(child)
          } else if (child.$children && child.$children.length) {
            search(child.$children, depth + 1)
          }
        }

        if (depth === 0) return results
      }

      try {
        return search(this.$children) || []
      } catch (e) {
        console.warn('[form-field] getInput error', e)
        return []
      }
    },

    validate (error = null) {
      this.field.error = error
      if (error) {
        // try to force validation so the UI shows the error
        if (typeof this.field.validate === 'function') this.field.validate(true)
        this.validateOnBlur = false
      } else {
        if (typeof this.field.validate === 'function') this.field.validate()
        this.validateOnBlur = true
      }
    },
  },
}
